import React from 'react'
import _ from 'underscore'
import moment from 'moment'
import { TriangleAlert, Trash2, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import EditEnquiryLearnerSubject from './edit'
import store from '../../../state/store'

class EnquiryLearnerSubjectsPartial extends React.Component {

  constructor(props) {
    super(props)
    
    this.state = {
      editing: false,
      editing_result: false,
      result_draft: ''
    }
  }

  onClick = () => {
    if (this.state.editing === false) {
      this.setState({editing: true})
    }
  }

  onSaved = () => {
    this.setState({editing: false})
  }

  hasResult() {
    return !!this.props.learner_subject.result || this.props.learner_subject.result === 0
  }

  formattedResult() {
    if (_.isFinite(this.props.learner_subject.result)) {
      return `${this.props.learner_subject.result}%`
    }
    return this.props.learner_subject.result
  }

  // A rough "when", e.g. "today", "yesterday", "7 Oct"; marks captured before timestamps existed have none.
  recordedAt() {
    const recordedAt = moment(this.props.learner_subject.result_recorded_at)

    return recordedAt.calendar(null, {
      sameDay: '[today]',
      lastDay: '[yesterday]',
      lastWeek: 'D MMM',
      sameElse: recordedAt.year() === moment().year() ? 'D MMM' : 'D MMM YYYY'
    })
  }

  recordedAtTitle() {
    if (!this.props.learner_subject.result_recorded_at) {
      return 'Click to change the latest mark'
    }

    const recordedAt = moment(this.props.learner_subject.result_recorded_at).format('ddd Do MMM YYYY [at] HH:mm')
    return `Captured ${recordedAt}. Click to change the latest mark`
  }

  onEditResult = () => {
    this.setState({editing_result: true, result_draft: String(this.props.learner_subject.result)})
  }

  onResultDraftChange = (event) => {
    this.setState({result_draft: event.target.value})
  }

  onResultKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.target.blur()
    }
    if (event.key === 'Escape') {
      this.setState({editing_result: false, result_draft: ''})
    }
  }

  onSaveResult = () => {
    const draft = this.state.result_draft.trim()
    const current = this.hasResult() ? String(this.props.learner_subject.result) : ''

    if (draft !== current) {
      store.emit('enquiry:learner_subjects:update', this.props.subject.enquiry_id, this.props.learner_subject.id, {
        subject_name: this.props.subject.name,
        subject_category_name: this.props.subject.subject_category_name,
        subject_level_name: this.props.subject.subject_level_name,
        syllabus: this.props.subject.syllabus,
        result: draft,
        focus_areas: this.props.learner_subject.focus_areas
      })
    }

    this.setState({editing_result: false, result_draft: ''})
  }

  renderResult() {
    if (this.hasResult() && !this.state.editing_result) {
      return (
        <Badge
          variant="secondary"
          size="md"
          className="cursor-pointer"
          title={this.recordedAtTitle()}
          onClick={this.onEditResult}
        >
          Latest mark: {this.formattedResult()}
          {this.props.learner_subject.result_recorded_at && (
            <span className="font-normal text-muted-foreground">· {this.recordedAt()}</span>
          )}
        </Badge>
      )
    }

    return (
      <label className="flex items-center gap-2 whitespace-nowrap text-xs font-medium">
        Latest mark
        <Input
          className="h-8 w-28 border-dashed border-border"
          placeholder="e.g. 54%"
          value={this.state.result_draft}
          autoFocus={this.state.editing_result}
          onChange={this.onResultDraftChange}
          onKeyDown={this.onResultKeyDown}
          onBlur={this.onSaveResult}
        />
      </label>
    )
  }

  onCreateLessonPlan = () => {
    store.emit('enquiry:lesson_plans:create', this.props.subject.enquiry_id, {
      enquiry_subject_ids: [this.props.learner_subject.enquiry_subject_id],
      enquiry_learner_ids: [this.props.learner_subject.enquiry_learner_id]
    })
  }

  onRemove = () => {
    store.emit('enquiry:learner_subjects:destroy', this.props.subject.enquiry_id, this.props.learner_subject.id)
  }

  render() {
    if (this.state.editing) {
      return (
        <EditEnquiryLearnerSubject
          enquiry_id={this.props.subject.enquiry_id}
          learner_subject_id={this.props.learner_subject.id}
          subject_name={this.props.subject.name}
          subject_category_name={this.props.subject.subject_category_name}
          subject_level_name={this.props.subject.subject_level_name}
          syllabus={this.props.subject.syllabus}
          result={this.props.learner_subject.result}
          focus_areas={this.props.learner_subject.focus_areas}
          country_code={this.props.country_code}
          onSaved={this.onSaved}
        />
      )
    }

    return (
      <div className="mb-2 rounded-[10px] border border-border bg-card hover:bg-muted/50">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-sm">
          <div className="flex min-w-48 flex-1 cursor-pointer items-center gap-2" onClick={this.onClick}>
            {!this.props.subject.known && (
              <span className="shrink-0" title='Unknown subject: check that the name, category and level match known information.'>
                <TriangleAlert className="size-4 text-amber-600" aria-hidden="true"/>
              </span>
            )}
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="font-medium">
                {this.props.subject.name}
                {this.props.subject.syllabus ? <span className="font-normal text-muted-foreground"> ({this.props.subject.syllabus})</span> : null}
              </span>
              <span className="text-xs text-muted-foreground">{this.props.subject.subject_level_name} {this.props.subject.subject_category_name}</span>
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" onClick={this.onCreateLessonPlan}>Create Lesson Plan</Button>
            <Button variant="outline" size="sm" title="Edit subject" onClick={this.onClick}><Pencil /> Edit</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="destructive" size="icon-sm" title="Remove subject"><Trash2 /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>Cancel</DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onSelect={this.onRemove}>Confirm</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-4 py-2 text-sm">
          {this.renderResult()}
          {this.props.learner_subject.focus_areas && (
            <span className="min-w-0 cursor-pointer text-xs italic text-muted-foreground" onClick={this.onClick}>
              {this.props.learner_subject.focus_areas}
            </span>
          )}
        </div>
      </div>
    )
  }
}

export default EnquiryLearnerSubjectsPartial
